import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, ArrowRight, Tag, FolderOpen, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { logger } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const currentCategory = searchParams.get('category') || '';
  const currentTag = searchParams.get('tag') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const postsPerPage = 6;

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchTags();
  }, [currentCategory, currentTag, currentPage]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentCategory) params.append('category', currentCategory);
      if (currentTag) params.append('tag', currentTag);
      params.append('limit', postsPerPage.toString());
      params.append('skip', ((currentPage - 1) * postsPerPage).toString());
      
      const response = await axios.get(`${API}/blog/posts?${params.toString()}`);
      setPosts(response.data.posts);
      setTotal(response.data.total);
    } catch (error) {
      logger.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/blog/tags`);
      setTags(response.data);
    } catch (error) {
      logger.error('Failed to fetch tags:', error);
    }
  };

  const handleCategoryFilter = (category) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    setSearchParams(params);
  };

  const handleTagFilter = (tag) => {
    const params = new URLSearchParams();
    if (tag) params.set('tag', tag);
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const totalPages = Math.ceil(total / postsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredPosts = searchQuery
    ? posts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="Blog - Technical Articles on Spectroscopy & AI"
        description="Read technical articles about FTIR spectroscopy, chromatography, and the future of AI in laboratory analysis. Learn from experts at TabedLab."
        keywords="spectroscopy blog, FTIR tutorial, chromatography guide, AI laboratory, scientific articles, spectral analysis tips"
        url="/blog"
        type="blog"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              Demo
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-cyan-400 font-medium"
            >
              Blog
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
            >
              Sign Up Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">TabedLab </span>
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Blog</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            Technical articles about spectroscopy, chromatography, and the future of AI in laboratory analysis.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              data-testid="blog-search-input"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-8">
              {/* Categories */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-cyan-400" />
                  Categories
                </h3>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={clearFilters}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        !currentCategory && !currentTag
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      }`}
                      data-testid="category-all"
                    >
                      All Posts
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.name}>
                      <button
                        onClick={() => handleCategoryFilter(cat.name)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                          currentCategory === cat.name
                            ? 'bg-cyan-500/20 text-cyan-400'
                            : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                        }`}
                        data-testid={`category-${cat.name.toLowerCase()}`}
                      >
                        <span>{cat.name}</span>
                        <span className="text-xs bg-slate-700 px-2 py-0.5 rounded-full">{cat.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tags */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-purple-400" />
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 10).map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => handleTagFilter(tag.name)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        currentTag === tag.name
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : 'bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                      data-testid={`tag-${tag.name.toLowerCase()}`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl p-6 text-center">
                <h3 className="text-lg font-semibold text-white mb-2">Try TabedLab Free</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Get 5 free analyses to experience AI-powered spectral analysis.
                </p>
                <Button
                  onClick={() => navigate('/register')}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                  data-testid="blog-sidebar-cta"
                >
                  Start Free
                </Button>
              </div>
            </aside>

            {/* Posts Grid */}
            <div className="lg:col-span-3">
              {/* Active Filters */}
              {(currentCategory || currentTag) && (
                <div className="mb-6 flex items-center gap-2">
                  <span className="text-slate-500 text-sm">Filtering by:</span>
                  {currentCategory && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                      <FolderOpen className="w-3 h-3" />
                      {currentCategory}
                    </span>
                  )}
                  {currentTag && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                      <Tag className="w-3 h-3" />
                      {currentTag}
                    </span>
                  )}
                  <button
                    onClick={clearFilters}
                    className="text-slate-500 hover:text-white text-sm ml-2"
                  >
                    Clear
                  </button>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-slate-400 text-lg">No blog posts found.</p>
                  {(currentCategory || currentTag || searchQuery) && (
                    <button
                      onClick={() => {
                        clearFilters();
                        setSearchQuery('');
                      }}
                      className="text-cyan-400 hover:underline mt-2"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    {filteredPosts.map((post) => (
                      <article
                        key={post.id}
                        className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all group cursor-pointer"
                        onClick={() => navigate(`/blog/${post.slug}`)}
                        data-testid={`blog-post-card-${post.slug}`}
                      >
                        {post.featured_image && (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.featured_image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          {/* Categories */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {post.categories?.slice(0, 2).map((cat) => (
                              <span
                                key={cat}
                                className="px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded-full"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                          
                          <h2 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          
                          <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          
                          <div className="flex items-center justify-between text-slate-500 text-sm">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(post.published_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {post.reading_time} min read
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className={currentPage === page 
                            ? "bg-cyan-500 text-white" 
                            : "border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
                          }
                        >
                          {page}
                        </Button>
                      ))}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} TabedLab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
